import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { ufChoice } from '@/lib/deadlines'
import { formatDateBr } from '@/lib/publication-inbox'
import { deadlineService, type DeadlineCalculation, type DeadlineUnit } from '@/services/deadline-service'
import { CalculationMemoryPopover } from './CalculationMemoryPopover'

/** Simulador de prazo (mesmo cálculo da API usado nas publicações). */
export function DeadlineCalculatorCard({ tribunal }: { tribunal: string }) {
  const choice = ufChoice(tribunal)
  const [disponibilizacao, setDisponibilizacao] = useState('')
  const [dias, setDias] = useState('15')
  const [unidade, setUnidade] = useState<DeadlineUnit>('UTEIS')
  const [uf, setUf] = useState('')
  const [result, setResult] = useState<DeadlineCalculation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setResult(await deadlineService.calculate({ tribunal, uf: uf || null, disponibilizacao, dias: Number(dias), unidade }))
    } catch (err) {
      setResult(null)
      setError((err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Erro ao calcular')
    } finally {
      setBusy(false)
    }
  }

  if (!tribunal) return <p className="text-sm text-muted-foreground">Escolha um tribunal para simular.</p>
  return (
    <div className="space-y-3">
      <form onSubmit={submit} aria-label="Simular prazo" className="flex flex-wrap items-end gap-2">
        <div><Label htmlFor="c-disp">Disponibilização</Label><Input id="c-disp" type="date" value={disponibilizacao} onChange={(e) => setDisponibilizacao(e.target.value)} required /></div>
        <div className="w-20"><Label htmlFor="c-dias">Dias</Label><Input id="c-dias" type="number" min={1} max={365} value={dias} onChange={(e) => setDias(e.target.value)} required /></div>
        <div className="w-36">
          <Label htmlFor="c-unidade">Contagem</Label>
          <NativeSelect id="c-unidade" value={unidade} onChange={(e) => setUnidade(e.target.value as DeadlineUnit)}>
            <option value="UTEIS">Dias úteis</option>
            <option value="CORRIDOS">Dias corridos</option>
          </NativeSelect>
        </div>
        {choice && !choice.fixed && (
          <div className="w-24">
            <Label htmlFor="c-uf">UF</Label>
            <NativeSelect id="c-uf" value={uf} onChange={(e) => setUf(e.target.value)}>
              <option value="">—</option>
              {choice.options.map((u) => <option key={u} value={u}>{u}</option>)}
            </NativeSelect>
          </div>
        )}
        <Button type="submit" loading={busy}>Calcular</Button>
      </form>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {result && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" aria-label="Resultado do cálculo">
          <span>Publicação <strong className="tabular-nums">{formatDateBr(result.publicacao)}</strong></span>
          <span>Início <strong className="tabular-nums">{formatDateBr(result.inicio)}</strong></span>
          <span>Fatal <strong className="tabular-nums">{formatDateBr(result.vencimento)}</strong></span>
          <span>Interno <strong className="tabular-nums">{formatDateBr(result.prazoInterno)}</strong></span>
          {result.vencimentoLegal && (
            <span className="text-destructive">Fatal pela leitura mais segura (dia “a conferir”); contando-o, {formatDateBr(result.vencimentoLegal)}</span>
          )}
          <CalculationMemoryPopover memoria={result.memoria} />
        </div>
      )}
    </div>
  )
}
